import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  HStack,
  Input,
  useDisclosure,
  Separator,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { BiPencil } from "react-icons/bi";
import FaceliftDialog from "@/components/Atoms/FaceliftDialog";
import FaceliftInput from "@/components/Atoms/FaceliftInput";
import Show from "@/components/Molecules/Show";
import FaceliftText from "@/components/Atoms/FaceliftText";
import FaceliftIconButton from "@/components/Atoms/FaceliftIconButton";
import { useGetInsurancesGroups, useGetInsurancesMl } from "@/hooks/service-hooks/insurances";
import FaceliftButton from "@/components/Atoms/FaceliftButton";
import { Tooltip } from "@/components/ui/tooltip";
import { LuCircleX } from "react-icons/lu";
import { BulbIcon } from "@/assets/icons";
import { colors } from "@/components/ui/theme";

interface ISmartInsuranceProps {
  placeholder?: string;
  toplabel?: string;
  required?: boolean;
  onConfirm?: (selection: { grouping: string; type: string; plan: { id: string, name: string } | null }) => void;
  defaultValue?: {
    grouping: string;
    type: string;
    plan: {
      id: string;
      name: string;
    }
  },
  error?: string;
}

interface InsuranceListItem {
  label: string;
  value: string;
}

interface InsuranceOptionListProps {
  name: string;
  value?: string;
  items: InsuranceListItem[];
  onValueChange: (value: string) => void;
  allowDeselect?: boolean;
}

const InsuranceOptionList = ({
  name,
  value,
  items,
  onValueChange,
  allowDeselect = false,
}: InsuranceOptionListProps) => (
  <Stack role="radiogroup" gap={1} align="stretch">
    {items.map((item) => {
      const checked = value === item.value;
      const nextValue = allowDeselect && checked ? "" : item.value;

      return (
        <Box
          as="label"
          key={item.value}
          role="radio"
          aria-checked={checked}
          tabIndex={0}
          minH="40px"
          px={3}
          py={2}
          display="flex"
          alignItems="center"
          gap={3}
          borderRadius="md"
          border="1px solid"
          borderColor={checked ? "brand.success" : "transparent"}
          bg={checked ? { _light: "green.50", _dark: "whiteAlpha.100" } : "transparent"}
          cursor="pointer"
          userSelect="none"
          transition="background 0.15s ease, border-color 0.15s ease"
          _hover={{ bg: { _light: "gray.50", _dark: "whiteAlpha.100" } }}
          _focusVisible={{ outline: "2px solid", outlineColor: "brand.success", outlineOffset: "2px" }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            onValueChange(nextValue);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onValueChange(nextValue);
          }}
        >
          <input
            aria-hidden="true"
            tabIndex={-1}
            name={name}
            type="radio"
            value={item.value}
            checked={checked}
            readOnly
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
            }}
          />
          <Box
            w="16px"
            h="16px"
            minW="16px"
            minH="16px"
            borderRadius="full"
            border="2px solid"
            borderColor={checked ? "brand.success" : "gray.400"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Show when={checked}>
              <Box w="8px" h="8px" borderRadius="full" bg="brand.success" />
            </Show>
          </Box>
          <FaceliftText
            as="span"
            fontSize="sm"
            lineHeight="1.35"
            textAlign="left"
            wordBreak="break-word"
          >
            {item.label}
          </FaceliftText>
        </Box>
      );
    })}
  </Stack>
);

const SmartInsurance = ({ 
  placeholder = "Search...", 
  onConfirm, 
  defaultValue, 
  toplabel, 
  required, 
  error,
}: ISmartInsuranceProps) => {
  const { open, onOpen, onClose } = useDisclosure();

  const [search, setSearch] = useState("");
  const [selectedData, setSelectedData] = useState<{
    grouping: string,
    type: string,
    plan: {
      id: string,
      name: string,
    } | null
  } | null>(null);
  const [appliedPlan, setAppliedPlan] = useState<{
    id: string,
    name: string
  } | null>(null);

  useEffect(() => {
    if (defaultValue) {
      setSelectedData({
        grouping: defaultValue.grouping,
        type: defaultValue.type,
        plan: defaultValue.plan?.id ? {
          id: defaultValue.plan.id,
          name: defaultValue.plan.name,
        } : null,
      });
      setAppliedPlan(defaultValue.plan?.id ? defaultValue.plan : null);
    }
  }, [
    defaultValue?.grouping,
    defaultValue?.type,
    defaultValue?.plan?.id,
    defaultValue?.plan?.name,
  ]);

  const { data: insuranceGroups, isLoading: insuranceGroupLoading } = useGetInsurancesGroups({ enabled: open });

  const { data: insurances, isLoading: insuranceLoading } = useGetInsurancesMl({
    page: 1,
    limit: 300,
    search,
    typeId: selectedData?.type || '',
    topId: defaultValue?.plan?.id || '',
    enabled: open,
  });

  const availableTypes = useMemo(() => {
    return insuranceGroups?.find((p) => p.id === selectedData?.grouping)?.types || [];
  }, [insuranceGroups, selectedData?.grouping]);

  const handleConfirm = () => {
    if (!selectedData?.plan) return;
    onConfirm?.({ grouping: selectedData?.grouping || '', type: selectedData?.type || '', plan: selectedData?.plan });
    setAppliedPlan(selectedData?.plan);
    onClose();
  };

  const handleClearInput = () => {
    setAppliedPlan(null);
    setSelectedData({ grouping: '', type: '', plan: null });
    onConfirm?.({ grouping: '', type: '', plan: null });
  };

  let nodeTimeout: NodeJS.Timeout;
  const handleSearch = (query: string) => {
    if (nodeTimeout) clearTimeout(nodeTimeout);
    nodeTimeout = setTimeout(() => {
      setSearch(query);
    }, 300);
  };

  return (
    <>
      <Show when={!!toplabel}>
        <FaceliftText 
          fontStyle={'italic'} 
          fontSize="sm" 
          mb="0.5"
        >
          {toplabel} 
          <FaceliftText as="span" color="red.500">{required ? '*' : ''}</FaceliftText>
        </FaceliftText>
      </Show>
      <Tooltip
        showArrow
        contentProps={{ css: { '--tooltip-bg': 'colors.brand.errors', display: error ? 'block' : 'none' } }}
        color="brand.muted"
        open={!!error}
        content={error}
        positioning={{ placement: 'top-center' }}
        portalled={false}
      >
        <HStack gap="2" align="center" justifyContent={'space-between'}>
          <Box flex={'1'}>
            <FaceliftInput 
              placeholder={placeholder} 
              name="input-insurance"
              id={appliedPlan?.id ?? 'insurance'}
              disabled 
              {...appliedPlan ? {
                endIcon: <FaceliftIconButton bg="transparent" mr={'-3'} color={'inherit'} onClick={handleClearInput}>
                  <LuCircleX />
                </FaceliftIconButton>
              } : {}}
              value={appliedPlan?.name ?? ''} 
            />
          </Box>
          <FaceliftIconButton
            variant="ghost"
            onClick={onOpen}
            aria-label="Edit insurance"
            bg="transparent"
            color={'inherit'}
            _hover={{ bg: "brand.solidGradient", color: 'white' }}
            title="Select Insurance"
          >
            <BiPencil />
          </FaceliftIconButton>
        </HStack>
      </Tooltip>

      <Box
        boxShadow={`0px 0px 10px 0px ${colors.aiBoxShadow}`}
        borderRadius={4}
        mt={3}
        px={4}
        py={2}
      >
        <FaceliftText
          fontSize="xs"
          fontWeight={700}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <BulbIcon />
          AI Suggestions
        </FaceliftText>
        <FaceliftText fontSize="xs" fontWeight={400}>
          Search by plan name, type, or category to quickly find the best insurance options for your patients 
        </FaceliftText>
      </Box>

      <FaceliftDialog open={open} onOpenChange={onClose} size="xl" title="Smart Insurance">
        <Box mb="4">
          <Input
            placeholder="Search plans..."
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Box>

        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap="0"
          border="1px solid"
          borderColor={{
            _light: "gray.200",
            _dark: "gray.700",
          }}
          borderRadius="md"
          overflow="hidden"
        >
          {/* Column 1 */}
          <Box p="4" borderRight={{ base: "none", md: "1px solid" }} borderColor={{ _light: "gray.200", _dark: "gray.700" }}>
            <FaceliftText fontWeight="semibold">Insurer Grouping</FaceliftText>
            <FaceliftText fontSize="sm" color="gray.500" mb="4">
              Select one option
            </FaceliftText>

            <Separator my={4}/>

            <Box height={'sm'} overflowY={'auto'}>
              <Show when={insuranceGroupLoading}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} mt={3}>
                  <Spinner size={'lg'}/>
                </Stack>
              </Show>
              <InsuranceOptionList
                name="insurance-grouping"
                value={selectedData?.grouping || ''}
                onValueChange={(e) => {
                  setSelectedData({
                    type: '',
                    plan: null,
                    grouping: e as string,
                  });
                }}
                items={insuranceGroups?.map((g) => ({ label: g.group, value: g.id })) ?? []}
                allowDeselect
              />
              <Show when={!insuranceGroupLoading && (insuranceGroups?.length === 0 || !insuranceGroups)}>
                <FaceliftText color="gray.500" fontSize="sm">
                  No insurer groupings available.
                </FaceliftText>
              </Show>
            </Box>
          </Box>

          {/* Column 2 */}
          <Box p="4" borderRight={{ base: "none", md: "1px solid" }} borderColor={{ _light: "gray.200", _dark: "gray.700" }}>
            <FaceliftText fontWeight="semibold">Insurance Type</FaceliftText>
            <FaceliftText fontSize="sm" color="gray.500" mb="4">
              Select one option
            </FaceliftText>

            <Separator my={4}/>

            <InsuranceOptionList
              name="insurance-type"
              value={selectedData?.type || ''}
              onValueChange={(e) => {
                setSelectedData((prev) => ({
                  grouping: prev?.grouping || '',
                  type: e as string,
                  plan: null,
                }));
              }}
              items={availableTypes.map((g) => ({ label: g.name, value: g.id }))}
              allowDeselect
            />
            <Show when={availableTypes.length === 0}>
              <FaceliftText color="gray.500" fontSize="sm">
                <Show when={!!selectedData?.grouping}>No types available for the selected grouping.</Show>
                <Show when={!selectedData?.grouping}>Please select a grouping to see available types.</Show>
              </FaceliftText>
            </Show>
          </Box>

          {/* Column 3 */}
          <Box p="4">
            <FaceliftText fontWeight="semibold">Plan Name</FaceliftText>
            <FaceliftText fontSize="sm" color="gray.500" mb="4">
              Select one option
            </FaceliftText>

            <Separator my={4}/>

            <Box height={'sm'} overflowY={'auto'}>
              <Show when={insuranceLoading}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} mt={3}>
                  <Spinner size={'lg'}/>
                </Stack>
              </Show>
              <InsuranceOptionList
                name="insurance-plan"
                value={selectedData?.plan?.id || ''}
                onValueChange={(e) => {
                  setSelectedData((prev) => ({
                    grouping: prev?.grouping || '',
                    type: prev?.type || '',
                    plan: e ? {
                      id: e as string,
                      name: (insurances || []).find((ins) => ins.id === e)?.name || '',
                    } : null,
                  }));
                }}
                items={(insurances || [])?.map((g) => ({ label: g.name, value: g.id }))}
                allowDeselect
              />
              <Show when={!insuranceLoading && (insurances?.length === 0 || !insurances)}>
                <FaceliftText color="gray.500" fontSize="sm">
                  <Show when={!!selectedData?.type}>
                    No insurance plans found for the selected type.
                  </Show>
                  <Show when={!selectedData?.type && !!search}>
                    No insurance plans matched your search.
                  </Show>
                  <Show when={!selectedData?.type && !search}>
                    No insurance plans available.
                  </Show>
                </FaceliftText>
              </Show>
            </Box>
          </Box>
        </Grid>
        <Flex w="full" justify="flex-end" gap="3" mt={3}>
          <FaceliftButton variant="outline" bg={'transparent'} color="inherit" onClick={onClose}>
            Cancel
          </FaceliftButton>
          <FaceliftButton colorScheme="blue" onClick={handleConfirm} disabled={!selectedData?.plan}>
            Confirm Selection
          </FaceliftButton>
        </Flex>
      </FaceliftDialog>
    </>
  );
};

export default SmartInsurance;
